import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Header } from '../components/Header';
import { ShareButtons } from '../components/ShareButtons';
import { FontPreview } from '../components/FontPreview';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Sparkles, 
  User,
  Calendar,
  Image as ImageIcon,
  Download,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FontSharePage() {
  const { fontId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  const [font, setFont] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasingPreview, setPurchasingPreview] = useState(null);
  
  // Get border style and color from URL params
  const borderStyle = searchParams.get('border') || 'none';
  const borderColor = searchParams.get('borderColor') || '#0A0A0A';

  useEffect(() => {
    fetchFont();
  }, [fontId]);

  // Update meta tags for social sharing
  useEffect(() => {
    if (font) {
      document.title = `${font.name} - FuntzGuntzAI`;
      
      // Update or create meta tags
      updateMetaTag('og:title', `${font.name} - AI Generated Font`);
      updateMetaTag('og:description', font.description);
      updateMetaTag('og:url', window.location.href);
      updateMetaTag('og:type', 'website');
      updateMetaTag('twitter:card', 'summary_large_image');
      updateMetaTag('twitter:title', `${font.name} - AI Generated Font`);
      updateMetaTag('twitter:description', font.description);
    }
    
    return () => {
      // Reset title on unmount
      document.title = 'FuntzGuntzAI - AI Custom Font Maker';
    };
  }, [font]);

  const updateMetaTag = (name, content) => {
    let meta = document.querySelector(`meta[property="${name}"]`) || 
               document.querySelector(`meta[name="${name}"]`);
    
    if (!meta) {
      meta = document.createElement('meta');
      if (name.startsWith('og:')) {
        meta.setAttribute('property', name);
      } else {
        meta.setAttribute('name', name);
      }
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  };

  const fetchFont = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/fonts/${fontId}/public`);
      setFont(response.data);
    } catch (error) {
      console.error('Failed to fetch font:', error);
      if (error.response?.status === 404) {
        toast.error('Font not found');
      } else {
        toast.error('Failed to load font');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewDownloadPurchase = async (format) => {
    if (!isAuthenticated) {
      toast.error('Please log in to purchase');
      navigate('/login');
      return;
    }

    setPurchasingPreview(format);
    try {
      const response = await axios.post(
        `${API_URL}/api/preview-download/checkout`,
        {
          font_id: fontId,
          format: format,
          border_style: borderStyle,
          border_color: borderColor,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Preview download checkout failed:', error);
      toast.error('Failed to start checkout');
    } finally {
      setPurchasingPreview(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0507]">
        <Header />
        <main className="pt-24 pb-16 px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Gothic Font Share Skeleton */}
            <div className="animate-gothic-fade space-y-8">
              <div className="skeleton-text w-20 h-6" />
              <div className="skeleton-card overflow-hidden">
                <div className="p-8 md:p-12 bg-[#160A0D]">
                  <div className="flex justify-between mb-6">
                    <div className="skeleton-text w-32 h-4" />
                    <div className="flex gap-2">
                      {[1, 2, 3, 4].map(i => (
                        <div key={i} className="skeleton-avatar w-8 h-8" />
                      ))}
                    </div>
                  </div>
                  <div className="skeleton-text w-64 h-12 mb-8" />
                  <div className="skeleton-preview aspect-square max-w-[500px] mx-auto" />
                </div>
                <div className="p-8 bg-[#220F13]">
                  <div className="flex gap-6">
                    <div className="skeleton-text w-32 h-4" />
                    <div className="skeleton-text w-24 h-4" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!font) {
    return (
      <div className="min-h-screen bg-[#0D0507]">
        <Header />
        <main className="pt-24 pb-16 px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl font-cinzel font-bold mb-4 text-[#F4EFE6]">
              Font Not Found
            </h1>
            <p className="text-[#C2B49A] mb-8 font-serif">
              This font may have been removed or doesn&apos;t exist.
            </p>
            <Button
              onClick={() => navigate('/generator')}
              className="btn-primary rounded-lg"
            >
              Create Your Own Font
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0507]" data-testid="font-share-page">
      <Header />
      
      <main className="pt-24 pb-16 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#C2B49A] hover:text-[#D4AF37] mb-8 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-cinzel uppercase tracking-wider">Back</span>
          </button>

          {/* Font Preview Card */}
          <div className="card-surface overflow-hidden rounded-lg">
            {/* Preview Section */}
            <div className="p-8 md:p-12 bg-[#160A0D] border-b border-[#D4AF37]/20">
              <div className="flex items-center justify-between mb-6">
                <span className="mono-label">
                  AI Generated Font
                </span>
                <ShareButtons font={font} />
              </div>
              
              <h1 
                className="text-4xl lg:text-5xl font-cinzel font-bold text-[#F4EFE6] mb-4"
                data-testid="font-name"
              >
                {font.name}
              </h1>
              
              {/* Square Font Preview with Border */}
              <div className="my-8">
                <FontPreview 
                  font={font}
                  borderStyle={borderStyle}
                  borderColor={borderColor}
                  showShareButtons={true}
                  className="mb-4"
                />
              </div>

              {/* Preview Download Section */}
              <div className="p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <ImageIcon className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-sm font-cinzel font-medium text-[#F4EFE6]">High-Res Preview Download</span>
                  <span className="text-xs text-[#D4AF37] font-cinzel font-bold ml-auto">$1.99</span>
                </div>
                <p className="text-xs text-[#C2B49A] mb-3 font-serif">
                  Download a stunning 5000×5000px preview image of this font{borderStyle !== 'none' ? ` with ${borderStyle} border` : ''}
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePreviewDownloadPurchase('jpg')}
                    disabled={purchasingPreview === 'jpg'}
                    className="flex-1 btn-secondary rounded-lg text-xs"
                    data-testid="download-jpg-btn"
                  >
                    {purchasingPreview === 'jpg' ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <Download className="w-3 h-3 mr-1" />
                    )}
                    JPG (5000×5000)
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePreviewDownloadPurchase('png')}
                    disabled={purchasingPreview === 'png'}
                    className="flex-1 btn-secondary rounded-lg text-xs"
                    data-testid="download-png-btn"
                  >
                    {purchasingPreview === 'png' ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <Download className="w-3 h-3 mr-1" />
                    )}
                    PNG Transparent
                  </Button>
                </div>
              </div>
            </div>

            {/* Info Section */}
            <div className="p-8 bg-[#220F13]">
              {/* Meta Info */}
              <div className="flex flex-wrap items-center gap-6 text-sm text-[#C2B49A] font-serif">
                {font.creator && (
                  <span className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Created by {font.creator.name}
                  </span>
                )}
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {formatDate(font.created_at)}
                </span>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-12 text-center">
            <p className="text-[#C2B49A] mb-4 font-serif">
              Want to create your own custom AI-generated font?
            </p>
            <Button
              onClick={() => navigate('/generator')}
              className="btn-primary rounded-lg"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Start Creating
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
